import {useCallback, useEffect, useState} from "react";
import {Stack} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {getLocale} from "../app/dateFormat";
import {DamsAccordion} from "../app/DamsAccordion";
import {PublishingStatusChip} from "./PublishingStatusChip";
import {publishingTagsSearchHandler} from "../publishing-tags/publishingTagsSearchHandler";
import {getHrPublishingState} from "./getHrPublishingState";
import Grow from "@mui/material/Grow";

export const PublishingStatusTable = ({t, model}) => {

    const locale = getLocale();

    const [publishingTags, setPublishingTags] = useState([]);
    const [data, setData] = useState([]);

    const getMappedTags = useCallback((modelTags) => {
        // Map the tag title to the new syntax:
        // M24 => Museum 24
        // NB => Nasjonalbiblioteket
        return modelTags.map((tag) => {
            const foundTag = publishingTags?.find(pt => pt.publishingType === tag.publishingType.replace('publishing-', ''));
            const {reference} = tag;

            if (!foundTag) {
                return tag;
            }

            return {
                ...tag,
                reference: {
                    ...reference,
                    title: foundTag.label
                }
            };
        });
    }, [publishingTags]);

    useEffect(() => {
        if (publishingTags.length > 0) {
            return;
        }
        (async () => {
            const systemTags = await publishingTagsSearchHandler();
            setPublishingTags(systemTags || []);
        })();
    }, [publishingTags.length]);

    useEffect(() => {
        const modelTags = model?.content?.publish?.tags || [];

        if (modelTags.length === 0) {
            return;
        }

        const mappedTags = getMappedTags(modelTags);
        setData(mappedTags);

        return () => {
            setData([]);
        };
    }, [model, publishingTags, getMappedTags]);

    return (
        <Box sx={{marginTop: '24px'}}>
            <Typography variant="h5">{t('publishingStatusHeading', 'Publiseringsstatus')}</Typography>

            {data?.length === 0 ?
                <Typography variant="body2">{t('publishingStatusNoData', 'Ingen data')}</Typography> : null}

            {data?.length > 0 &&
                <Grow in={true} timeout={300}>
                    <Stack direction="column" sx={{border: '1px solid #ddd', borderRadius: '4px', padding: '0'}}>
                        {data.map((tag) => (
                            <DamsAccordion key={tag.publishingType}
                                           title={
                                               <Stack
                                                   direction="row"
                                                   sx={{
                                                       justifyContent: "space-between",
                                                       alignItems: "center",
                                                       minWidth: '100%'
                                                   }}>
                                                   {tag.reference.title}
                                                   <PublishingStatusChip
                                                       t={t}
                                                       status={tag.sync.phases[tag.sync.phases.length - 1].status}/>
                                               </Stack>
                                           }
                                           baseKey={`tag-${tag.publishingType}`}>
                                <Box sx={{marginBottom: '8px'}}>
                                    <Typography component="div" variant="caption">
                                        {t('publishingStatusUser', 'Bruker')}
                                    </Typography>
                                    <Typography component="div"
                                                variant="caption">{tag.initiator.createdByName}</Typography>
                                </Box>

                                <Box sx={{
                                    minWidth: '100%',
                                }}>
                                    <Typography component="div" variant="caption">
                                        {t('publishingStatusHistory', 'Historikk')}
                                    </Typography>
                                    {[...tag.sync.phases]
                                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                                        .map((phase) => {
                                                // Create the Date once to avoid double instantiation.
                                                const createdAt = new Date(phase.createdAt);
                                                return <Stack direction="row" key={phase.createdAt} sx={{
                                                    minWidth: '100%',
                                                }}>
                                                    <Typography variant="caption" component="div" sx={{
                                                        whiteSpace: 'nowrap',
                                                        flexShrink: 0
                                                    }}>
                                                        {createdAt.toLocaleDateString(locale.code, {
                                                            year: 'numeric',
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                        })}
                                                        &nbsp;
                                                        -
                                                        &nbsp;
                                                        {createdAt.toLocaleTimeString(locale.code, {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            second: '2-digit'
                                                        })}
                                                    </Typography>
                                                    <Typography component="div" variant="caption" sx={{
                                                        minWidth: '200px',
                                                        marginLeft: '16px'
                                                    }}>
                                                        {getHrPublishingState(t, '', phase.status, false)}
                                                    </Typography>
                                                </Stack>;
                                            }
                                        )}
                                </Box>
                            </DamsAccordion>
                        ))}
                    </Stack>
                </Grow>
            }
        </Box>
    );

};